import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Displays "BUMPR PSA Guard – [Variant]" and updates the variant name (text +
 * color) instantly, without a page reload, whenever a new variant is selected.
 * Follows the same reactive pattern as the theme's product-price/product-sku
 * components: listen for the section's productSelect event and read the new
 * variant straight off the event's resolved resource.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} variantName
 *
 * @extends {Component<Refs>}
 */
class ProductVariantTitle extends Component {
  requiredRefs = ['variantName'];

  static colors = {
    Ruby: '#D92F3A',
    Solar: '#F6D33B',
    Azur: '#1E8DFF',
    Jade: '#1F8B4C',
    Tempest: '#CFEAFF',
    Onyx: '#111111',
    Muse: '#B88DFF',
    Coral: '#FF7A7A',
  };

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('.shopify-section, dialog');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('.shopify-section, dialog');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        const variantTitle = detail?.resource?.title;
        if (!variantTitle) return;

        this.refs.variantName.textContent = variantTitle;
        this.refs.variantName.style.color = ProductVariantTitle.colors[variantTitle] || '';
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-variant-title] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-variant-title')) {
  customElements.define('product-variant-title', ProductVariantTitle);
}
